import {
	Badge, Checkbox, IconButton, Paper, Table, TableBody,
	TableCell, TableContainer, TableHead, TableRow, Dialog, Button, Chip,
	TableSortLabel, Tooltip, Menu, Slider,
} from '@mui/material';
import { ReactComponent as NarrowSvg } from "assets/narrow.svg";
import { ReactComponent as SpreadSvg } from "assets/spread.svg";
import Icon from "components/icon/icon";
import Input from "components/input/input";
import MapSettings from "./mapSettings/map-settings"
import FilterEvents from "./filterEvents/filter-events";
import { LeftPanel, RightPanel } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { MapContext } from "helpers/map";
import { EventData } from "helpers/models";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconFilterSvg } from "assets/icon_filter.svg";
import { ReactComponent as EventsLinkedSvg } from "assets/icon_events_linked.svg";
import { ReactComponent as MapSettingSvg } from "assets/map_setting.svg";
import { ReactComponent as EventNodataSvg } from "assets/icon_event_nodata.svg";
import { getEventsLegends } from "api/layer";
import Svg from "components/svg/svg";
import TryAgain from "components/tryAgain/tryAgain";
import styles from "./events.module.scss";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { clearTcTrack, addTcTrack } from './tc-track/tc-track';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';


dayjs.extend(utc);
dayjs.extend(timezone);

interface Direction {
	type: "asc" | "desc";
	subType: "asc" | "desc";
	name: "asc" | "desc";
	eventDateTime: "asc" | "desc";
	hemisphere: "asc" | "desc";
}

export default function Events() {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const { AMap, map } = useContext(MapContext);
	const [eventsNameSearch, setEventsNameSearch] = useState("");
	const [data, setData] = useState<any[]>([]);
	const [active, setActive] = useState<keyof Direction>("eventDateTime");
	const [direction, setDirection] = useState<Direction>({ type: "asc", subType: "asc", name: "asc", eventDateTime: "asc", hemisphere: "asc" });

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [opacityAnchorEl, setOpacityAnchorEl] = useState<null | HTMLElement>(null);
	const [infoAnchorEl, setInfoAnchorEl] = useState<null | HTMLElement>(null);
	const [filterEventsEl, setFilterEventsEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const opacityOpen = Boolean(opacityAnchorEl);
	const infoOpen = Boolean(infoAnchorEl);
	const filterEventsOpen = Boolean(filterEventsEl);

	const [iconCurrent, setIconCurrent] = useState<any>(null);

	const [filterInvisible, setFilterInvisible] = useState(true);

	const handleFilterEventsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setFilterEventsEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleFilterEventsClose = () => {
		setFilterEventsEl(null);
	};

	const handleApply = (gData: any, aData: any) => {
	};

	const handleSort = (property: keyof Direction) => {
		if (!data) {
			return;
		}

		if (!property) {
			return;
		}

		setActive(property);

		direction[property] = direction[property] === "asc" ? "desc" : "asc";

		setDirection({ ...direction });

		let arr = [...context.eventsList!];
		arr.sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			const aValue = a?.[property];
			const bValue = b?.[property];

			if (aValue === undefined) {
				return direction[property] === "asc" ? -1 : 1;
			} else if (bValue === undefined) {
				return direction[property] === "asc" ? 1 : -1;
			}

			if (aValue < bValue) {
				return direction[property] === "asc" ? 1 : -1;
			} else if (aValue > bValue) {
				return direction[property] === "asc" ? -1 : 1;
			}
			return 0;
		});

		setData([...arr]);
	};

	const handleCreatDefaultData = () => {
		let arr = [...context.eventsList!];
		arr.sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			const aValue = a?.[active];
			const bValue = b?.[active];

			if (aValue === undefined) {
				return direction[active] === "asc" ? -1 : 1;
			} else if (bValue === undefined) {
				return direction[active] === "asc" ? 1 : -1;
			}

			if (aValue < bValue) {
				return direction[active] === "asc" ? 1 : -1;
			} else if (aValue > bValue) {
				return direction[active] === "asc" ? -1 : 1;
			}

			return 0;
		});

		setData([...arr]);
	};

	const toggleLocation = (evt: any, item: EventData): void => {
		evt?.stopPropagation();
		if (!item.checked && context.selectedEventsList.length >= 5) {
			dispatch({
				type: AppAction.addSnackBarParams,
				payload: {
					title: "eventsLimitAlert",
					titleIcon: "error",
					titleIconColor: "#fff"
				}
			})
			return;
		}
		let data: any = []
		if (!item.checked) {
			addTcTrack(item.id, map, t, context.showTcTrack);
			data = context.eventsList?.map((i: EventData) => {
				if (i.id === item.id) {
					i.checked = true;
					const isCross180 = i.extent.minimumLongitude > i.extent.maximumLongitude;
					const southWest = new AMap.LngLat(i.extent.minimumLongitude, i.extent.minimumLatitude, isCross180);
					const northEast = new AMap.LngLat(isCross180 ? i.extent.maximumLongitude + 360 : i.extent.maximumLongitude, i.extent.maximumLatitude, isCross180);
					let myBounds = new AMap.Bounds(southWest, northEast);
					map?.setBounds(myBounds, true, [20, 20, 20, 20]);
					dispatch({ type: AppAction.setCurrentEventsLocationInfo, payload: i });
					context.rightPanel === RightPanel.Empty && dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Events });
					if (i.layerObj) {
						i.layerObj.show()
						dispatch({ type: AppAction.updateEventsLegend, payload: i });
					} else {
						let layerObj = new AMap.TileLayer({
							tileUrl: `${(window as any).amapLayerUrl}/risklayer-api/catnet/tiles/${i.layerId}/tile/[z]/[x]/[y]?X-Token=${sessionStorage.getItem("token")}`,
							opacity: i.opacity,
						});
						i.layerObj = layerObj;
						map.addLayer(layerObj);
						getEventsLegends({ layers: i.layerId }).then(async (res: any) => {
							if (res.legends && res.legends.length > 0) {
								dispatch({ type: AppAction.addEventsLegend, payload: { ...res.legends[0], layerInfo: i } })
							}
						});
					}
					dispatch({ type: AppAction.setSelectedEventsList, payload: [...context.selectedEventsList, i] });
					// dispatch({ type: AppAction.setSearchMapCenter, payload: undefined });
				}
				return i
			});
		} else {
			clearTcTrack(item.id, map)
			data = context.eventsList?.map((i: EventData) => {
				if (i.id === item.id) {
					i.checked = false;
					i.layerObj?.hide();
				}
				return i;
			})

			context.eventsLegend?.forEach((legentItem) => {
				if (legentItem.layerInfo.id === item.id) {
					legentItem.layerInfo.checked = false;
					legentItem.layerInfo.layerObj.hide();
					dispatch({ type: AppAction.updateEventsLegend, payload: legentItem.layerInfo });
				}
			})

			let selectedEventsList = context.selectedEventsList.filter((event: EventData) => event.id !== item.id);
			dispatch({ type: AppAction.setSelectedEventsList, payload: selectedEventsList });

			(selectedEventsList.length === 0 && context.rightPanel === RightPanel.Events) && dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
			(selectedEventsList.length === 0) && dispatch({ type: AppAction.setCurrentEventsLocationInfo, payload: undefined });
		}
		dispatch({
			type: AppAction.setEventsList,
			payload: data,
		});
	};

	const handleClickLinked = (item: EventData) => {
		context.eventsList?.forEach((i: EventData) => {
			if (item.linkedEventIds.includes(i.id)) {
				toggleLocation(undefined, i);
			}
		});
	}

	const handleChangeOpacity = (event: any, value: any) => {
		let layerInfo = { ...iconCurrent, opacity: value };
		let list = context.eventsList!.map((i: EventData) => {
			if (i.id === layerInfo.id) {
				i.opacity = value;
				i.layerObj && i.layerObj.setOpacity(value);
			}
			return i;
		});
		dispatch({ type: AppAction.setEventsList, payload: list });
		setIconCurrent(layerInfo);
	}

	const handleChangeEventsSize = () => {
		dispatch({ type: AppAction.setLocationsSize, payload: !context.locationsSize });
	};

	const goBlack = () => {
		dispatch({
			type: AppAction.setEventsFilterForm,
			payload: {
				fromDateTime: dayjs(new Date().setMonth(new Date().getMonth() - 3)).format("YYYY-MM-DD"),
				toDateTime: dayjs(new Date()).format("YYYY-MM-DD"),
				isShowAll: false,
				type: ["Cyclone", "Winterstorm", "Earthquake", "Flood", "Rainfall"],
				isNotRequest: false,
			}
		});
	}

	const handleRetry = () => {
		dispatch({ type: AppAction.setTryAgainType, payload: "eventsList" })
	}

	useEffect(() => {
		if (context.eventsList && context.eventsList.length > 0) {
			handleCreatDefaultData();
		} else {
			setData([]);
		}
	}, [context.eventsList, context.eventsFilterForm]);

	useEffect(() => {
		let fromDateTime = dayjs(new Date().setMonth(new Date().getMonth() - 3)).format("YYYY-MM-DD");
		let toDateTime = dayjs(new Date()).format("YYYY-MM-DD");
		if (fromDateTime === context.eventsFilterForm.fromDateTime
			&& toDateTime === context.eventsFilterForm.toDateTime
			&& !context.eventsFilterForm.isShowAll
			&& context.eventsFilterForm.type.length === 5
		) {
			setFilterInvisible(true);
		} else {
			setFilterInvisible(false);
		}
	}, [context.eventsFilterForm])

	const handleRenderTableOrNoData = () => {
		if (!context.eventsList) {
			return null
		}
		let arr = data.filter((item: any) => (eventsNameSearch === "" || item.name.toLowerCase().includes(eventsNameSearch.toLowerCase()))
			&& context.eventsFilterForm.type.includes(item.type));

		return <>
			{arr.length === 0 ?
				(<div className={styles.filterNoData}>
					<EventNodataSvg />
					<p className="text-[14px]">{t("events.noEventsFound")}</p>
					<p className="text-[12px]">{t("events.tryToClearFilter")}</p>
					<Button onClick={() => {
						setEventsNameSearch("")
						if (context.eventsFilterForm.locationInfo) {
							dispatch({
								type: AppAction.setEventsFilterForm,
								payload: {
									fromDateTime: dayjs("1876-01-01").format("YYYY-MM-DD"),
									toDateTime: dayjs(new Date()).format("YYYY-MM-DD"),
									isShowAll: true,
									type: ["Cyclone", "Winterstorm", "Earthquake", "Flood", "Rainfall"],
									longitude: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.longitudeWgs : context.searchLocationInfo.en.location.split(",")[0],
									latitude: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.latitudeWgs : context.searchLocationInfo.en.location.split(",")[1],
									country: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.country : context.searchLocationInfo.en.country,
									oversea: context.currentMultipleLocationInfo ? true : context.searchLocationInfo.en.oversea,
									spatial: context.currentMultipleLocationInfo ? "wgs" : context.searchLocationInfo.en.spatial,
									locationInfo: context.currentMultipleLocationInfo ?? context.searchLocationInfo,
									isNotRequest: true,
								}
							});
						} else {
							dispatch({
								type: AppAction.setEventsFilterForm,
								payload: {
									...context.eventsFilterForm,
									isNotRequest: false,
									fromDateTime: dayjs(new Date().setMonth(new Date().getMonth() - 3)).format("YYYY-MM-DD"),
									toDateTime: dayjs(new Date()).format("YYYY-MM-DD"),
									isShowAll: false,
									type: ["Cyclone", "Winterstorm", "Earthquake", "Flood", "Rainfall"]
								}
							});
						}
					}} color="success">{t("buttons.clearFilterAndRetryYourSearch")}</Button>
				</div>)
				:
				(<Paper
					sx={{ width: "100%", overflow: "hidden", position: "relative", boxShadow: "none", flex: 1, display: "flex", flexDirection: "column" }}
					className={styles.table}
				>
					<TableContainer sx={{
						height: "100%",
						overflow: 'hidden',
						flex: 1,
						"& [data-overlayscrollbars-viewport]": {
							display: "flex",
							flexDirection: "column"
						}
					}} >
						<OverlayScrollbarsComponent
							style={{ height: '100%' }}      // 占满父容器
							options={{
								overflow: { x: 'scroll', y: 'scroll' }, // 明确设置滚动方向
								scrollbars: {
									autoHide: 'leave',        // 鼠标移入/移出时自动隐藏
									autoHideDelay: 0,        // 需要更跟手可设 0，默认有延迟
									theme: 'os-theme-dark'   // 可选：主题（或自定义 CSS）
								}
							}}
							defer     // 延迟初始化，提升复杂容器下的稳定性
						>
							<div style={{ flex: 1 }}>
								<Table
									stickyHeader
									aria-label="sticky  table"
									size="small"
									style={{
										tableLayout: "fixed",
										width: "100%",
										minWidth: "100%"
									}}
								>
									<TableHead>
										<TableRow>
											<TableCell
												style={{ width: 40 }}
												align="left"
											></TableCell>

											<TableCell
												align="center"
												style={{
													width: 46,
													whiteSpace: "nowrap",
													padding: "6px"
												}}
											>
												<TableSortLabel
													active={active === "type"}
													onClick={() => handleSort("type")}
													direction={(direction as any)["type"]}
												>
													{t("events.type")}
												</TableSortLabel>
											</TableCell>
											<TableCell
												align="left"
												style={{
													width: 110,
													whiteSpace: "nowrap",
												}}
											>
												<TableSortLabel
													active={active === "subType"}
													onClick={() => handleSort("subType")}
													direction={(direction as any)["subType"]}
												>
													{t("events.subtype")}
												</TableSortLabel>
											</TableCell>
											<TableCell
												align="left"
												style={{
													width: 116,
													whiteSpace: "nowrap",
												}}
											>
												<TableSortLabel
													active={active === "name"}
													onClick={() => handleSort("name")}
													direction={(direction as any)["name"]}
												>
													{t("events.name")}
												</TableSortLabel>
											</TableCell>
											<TableCell
												align="left"
												style={{
													width: 90,
													whiteSpace: "nowrap",
												}}
											>
												<TableSortLabel
													active={active === "eventDateTime"}
													onClick={() => handleSort("eventDateTime")}
													direction={(direction as any)["eventDateTime"]}
												>
													{t("events.date")}
												</TableSortLabel>
											</TableCell>
											<TableCell
												align="left"
												style={{
													width: 115,
													whiteSpace: "nowrap",
												}}
											>
												<TableSortLabel
													active={active === "hemisphere"}
													onClick={() => handleSort("hemisphere")}
													direction={(direction as any)["hemisphere"]}
												>

													{t("events.hemisphere")}
												</TableSortLabel>
											</TableCell>
											<TableCell style={{ padding: 0, width: 0 }}></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{arr.map(
											(item: any) => (
												<React.Fragment key={item.id}>
													<TableRow
														id={item.id}
														selected={item.checked}
														className={styles.row}
														sx={{
															"&.Mui-selected,&.Mui-selected:hover": {
																backgroundColor: "rgba(14, 138, 63, 0.08)!important",
															},
															"&.Mui-selected .icons-set": {
																background: "linear-gradient(90deg,rgba(236,246,240,0) 0,#ecf6f0 24px)!important",
															},
														}}
														onClick={() => {
															if (item.checked) {
																const isCross180 = item.extent.minimumLongitude > item.extent.maximumLongitude;
																const southWest = new AMap.LngLat(item.extent.minimumLongitude, item.extent.minimumLatitude, isCross180);
																const northEast = new AMap.LngLat(isCross180 ? item.extent.maximumLongitude + 360 : item.extent.maximumLongitude, item.extent.maximumLatitude, isCross180);
																let myBounds = new AMap.Bounds(southWest, northEast);
																map?.setBounds(myBounds, true, [20, 20, 20, 20]);
																dispatch({ type: AppAction.setCurrentEventsLocationInfo, payload: item });
																dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Events });
															}
														}}
													>
														<TableCell
															style={{
																padding: 0,
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
															align="left"
														>
															<Checkbox
																size="small"
																color="success"
																checked={item.checked}
																onClick={(evt: any) => toggleLocation(evt, item)}
															/>
														</TableCell>

														<TableCell
															align="center"
															style={{
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
														>
															<Tooltip title={t(`events.${(item.type === "Cyclone" || item.type === "Winterstorm") ? "Storm" : item.type}`)} placement="top">
																{Svg({ icon: item.type, style: { width: "24px", height: "24px" } })()}
															</Tooltip>
														</TableCell>
														<TableCell
															align="left"
															style={{
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
														>
															{t("events." + item.type)}
														</TableCell>
														<TableCell
															align="left"
															style={{
																overflow: "hidden",
																textOverflow: "ellipsis",
																whiteSpace: "nowrap",
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
														>
															<Tooltip title={item.name} placement="top">
																<span>{item.name}</span>
															</Tooltip>
														</TableCell>
														<TableCell
															align="left"
															style={{
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
														>
															{dayjs(item.eventDateTime).tz('Asia/Shanghai').format('YYYY-MM-DD')}
														</TableCell>
														<TableCell
															align="left"
															style={{
																borderBottom: item.checked && item.linkedEventIds.length > 0 && "none"
															}}
														>
															{item.hemisphere}
														</TableCell>
														<TableCell style={{ padding: 0, position: "relative" }}>
															<div
																role="button"
																tabIndex={0}
																className={`${styles.icons} icons-set`}
																onClick={(evt) => evt.stopPropagation()}
																style={{ display: iconCurrent && (iconCurrent.id === item.id) ? "flex" : "" }}
															>

																{context.showTcTrack && item.type === "Cyclone" &&
																	<Tooltip title={t("events.tropicalCycloneTrackLayer")} placement="top">
																		<Chip
																			label="TcTrack"
																			size="small"
																			sx={{
																				background: "#0f4dbc",
																				color: "#fff",
																				marginRight: "8px",
																				fontSize: "12px"
																			}}
																		/>
																	</Tooltip>}

																<IconButton
																	style={{ width: "36px", height: "36px" }}
																	onClick={(event) => {
																		setIconCurrent(item)
																		setInfoAnchorEl(event.currentTarget);
																	}}
																>
																	<span className="material-icons">info</span>
																</IconButton>
																<IconButton
																	style={{ width: "36px", height: "36px" }}
																	onClick={(event) => {
																		setIconCurrent(item)
																		setOpacityAnchorEl(event.currentTarget);
																	}}
																>
																	<span className="material-icons">opacity</span>
																</IconButton>
																{/* <Icon
																	icon="opacity"
																	onClick={(event) => {
																		setIconCurrent(item)
																		setOpacityAnchorEl(event.currentTarget);
																	}}
																/> */}
															</div>
														</TableCell>
													</TableRow>
													{item.checked && item.linkedEventIds.length > 0 && (
														<TableRow
															selected
															className={styles.row}
															sx={{
																"&:hover": {
																	backgroundColor: "#d8d8d833!important"
																},
																"&.Mui-selected,&.Mui-selected&:hover": {
																	backgroundColor: "rgba(14, 138, 63, 0.08)!important",
																},
																"&.Mui-selected .delete-set": {
																	background: "linear-gradient(90deg, rgba(238, 238, 238, 0) 0, #ecf6f0 24px)!important",
																},
															}}
														>
															<TableCell
																align="left"
																style={{
																	padding: "0 8px 0 68px",
																	cursor: "auto",

																}}
																colSpan={6}
															>
																<div
																	className='flex items-center cursor-pointer'
																	onClick={() => handleClickLinked(item)}
																	role="button"
																	tabIndex={0}
																>
																	<EventsLinkedSvg className='w-[24px] h-[24px] text-[24px] mr-[6px]' />
																	<span className='text-[green]'>{t(`events.linked${item.type}`, { name: item.name, type: item.type })}</span>
																</div>
															</TableCell>
														</TableRow>
													)}
												</React.Fragment>
											)
										)}
									</TableBody>
								</Table>
							</div>
							<div
								className={styles.pagination}
							>
								{arr.length}
								{t("events.events")}
							</div>
						</OverlayScrollbarsComponent>
					</TableContainer>
				</Paper >)
			}
		</>
	}

	const handleRenderList = () => {
		if (context.eventsListStatus === "1" || context.eventsList?.length != data.length) {
			return (
				<div className={styles.loading}>
					<div className="loading loading-spinner loading-lg"></div>
					<div>{t("events.loadingEvents")}</div>
				</div>
			)
		} else if (context.eventsListStatus === "3") {
			return <TryAgain handleRetry={handleRetry} />
		} else {
			return handleRenderTableOrNoData()
		}
	}

	return (
		<div
			className={styles.set}
			style={{ width: context.locationsSize ? "800px" : "400px" }}
		>
			<div className={styles.header}>
				<div className={styles.title}>
					<div className="flex items-center">
						{context.eventsFilterForm.locationInfo && (
							<Icon
								icon="arrow_back"
								style={{
									flexShrink: 0,
									width: "36px",
									height: "36px",
									padding: "6px",
								}}
								onClick={goBlack}
							/>
						)}
						<span>{t("events.title")}</span>
						<div
							onClick={() => dispatch({ type: AppAction.setFathomIconDialog, payload: true })}
							className="ml-[10px] w-[21px] h-[21px]  rounded-full bg-[#787878] flex items-center justify-center cursor-pointer"
							role="button"
							tabIndex={0}
						>
							<div className="material-icons text-[16px] text-[#fffc]" >star</div>
						</div>
					</div>
					{!context.eventsFilterForm.locationInfo && <div className={styles.close}>
						<Icon onClick={() => setLeftPanel(dispatch, LeftPanel.Empty)} />
					</div>}
				</div>
				{context.eventsList && (
					<div className="flex items-center justify-between mx-[10px] mb-[16px]">
						<div className={styles.search}>
							<Input
								icon="search"
								placeholder={t("events.searchPlaceholder")}
								isClose={true}
								onClose={() => {
									setEventsNameSearch("");
								}}
								onChange={(e) => {
									setEventsNameSearch(e.target.value);
								}}
								value={eventsNameSearch}
							/>

						</div>
						<div className={styles.switch}>
							{context.locationsSize ? (
								<Tooltip title={t("tooltip.collapse")} placement="top">
									<IconButton
										onClick={handleChangeEventsSize}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
									>
										<NarrowSvg style={{ width: "20px", height: "17px" }} />
									</IconButton>
								</Tooltip>
							) : (
								<Tooltip title={t("tooltip.expand")} placement="top">
									<IconButton
										onClick={handleChangeEventsSize}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
									>
										<SpreadSvg style={{ width: "20px", height: "17px" }} />
									</IconButton>
								</Tooltip>
							)}

							<Tooltip title={t("events.eventsSettings")} placement="top">
								<IconButton
									onClick={(event) => {
										setAnchorEl(event.currentTarget)
									}}
									color="success"
									sx={{
										width: "36px",
										height: "36px",
										"&:hover": {
											background: "rgba(0, 0, 0, 0.04)",
										},
									}}
									style={{
										background: open ? "#c1e5ca" : "",
									}}
								>
									<MapSettingSvg
										className="focus:outline-none w-[20px] h-[20px]"
									/>
								</IconButton>
							</Tooltip>

							<Tooltip title={t("events.filterEvents")} placement="top">
								<Badge
									overlap="circular"
									variant="dot"
									invisible={filterInvisible}
									sx={{
										".MuiBadge-badge": { background: "#ffa02f" },
									}}
								>
									<IconButton
										onClick={handleFilterEventsClick}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
										style={{
											background: filterEventsOpen || !filterInvisible ? "#c1e5ca" : "",
										}}
									>
										<IconFilterSvg
											style={{
												fill: filterEventsOpen || !filterInvisible ? "green" : "",
											}}
											className="focus:outline-none w-[20px] h-[12px]"
										/>
									</IconButton>
								</Badge>
							</Tooltip>
						</div>
					</div>
				)}
			</div>
			{context.eventsFilterForm.locationInfo && <div className={styles.locationInfo}>
				<div className={styles.locationInfoHeader}>
					<h6>{context.eventsFilterForm.locationInfo.name ?? context.eventsFilterForm.locationInfo[context.language].formatted_address}</h6>
					<Icon onClick={goBlack} />
				</div>
				<div className={styles.locationInfoContent}>
					<div>
						{context.eventsFilterForm.locationInfo.place ?? context.eventsFilterForm.locationInfo[context.language].formatted_address}
					</div>
					<div className='my-[4px]'>
						{(() => {
							const locInfo = context.eventsFilterForm.locationInfo;
							if (locInfo.coordinateSystem === "GCJ02" && locInfo.longitudeGcj && locInfo.latitudeGcj) {
								return `${locInfo.longitudeGcj}, ${locInfo.latitudeGcj}`;
							} else if (locInfo.longitudeWgs && locInfo.latitudeWgs) {
								return `${locInfo.longitudeWgs}, ${locInfo.latitudeWgs}`;
							} else {
								return locInfo[context.language]?.location;
							}
						})()}
					</div>
				</div>
			</div>}

			{handleRenderList()}

			{
				iconCurrent && <Menu
					anchorEl={opacityAnchorEl}
					open={opacityOpen}
					onClose={() => {
						setIconCurrent(null);
						setOpacityAnchorEl(null);
					}}
				>
					<div className="px-4 py-2 w-[200px]">
						<div className="text-[14px] my-[4px]">{t("layers.opacity")}: {((iconCurrent.opacity || 0) * 100).toFixed(0)}%</div>
						<Slider
							value={iconCurrent.opacity}
							size="small"
							max={1}
							min={0}
							step={0.01}
							color="success"
							onChange={handleChangeOpacity}
						/>
					</div>
				</Menu>
			}

			{
				iconCurrent && <Dialog
					open={infoOpen}
					onClose={() => {
						setIconCurrent(null);
						setInfoAnchorEl(null);
					}}

					sx={{
						"& .MuiDialog-paper": {
							width: "600px",
							maxWidth: "80vw",
							maxHeight: "85vh",
						}
					}}
				>
					<div style={{ padding: "24px 24px 18px" }}>
						<div
							style={{
								fontStyle: "normal",
								fontWeight: 500,
								fontSize: "16px",
								lineHeight: "19px",
								letterSpacing: ".2px",
								color: "#000000de",
							}}
						> {t("events.eventDetails")}</div>
						<div className={styles.eventDetails}>
							<div>
								<span>{t("events.peril")}</span>
								<span>{t("events." + iconCurrent.type)}</span>
							</div>
							<div>
								<span>{t("events.date")}</span>
								<span>{iconCurrent.type === "Earthquake" ? `${dayjs(iconCurrent.eventDateTime).utc().format('YYYY-MM-DD HH:mm:ss')} UTC` : dayjs(iconCurrent.eventDateTime).tz('Asia/Shanghai').format('YYYY-MM-DD')}</span>
							</div>
							<div>
								<span>{t("events.lastUpdate")}</span>
								<span>{iconCurrent.type === "Earthquake" ? `${dayjs(iconCurrent.updateDateTime).utc().format('YYYY-MM-DD HH:mm:ss')} UTC` : dayjs(iconCurrent.updateDateTime).tz('Asia/Shanghai').format('YYYY-MM-DD')}</span>
							</div>
							<div>
								<span>{t("events.location")}</span>
								<span>{`${iconCurrent.longitude}, ${iconCurrent.latitude}`}</span>
							</div>
							<div>
								<span>{t("events.description")}</span>
								<span>{iconCurrent.description}</span>
							</div>
							<div>
								<span>{t("events.source")}</span>
								<span>{iconCurrent.source}</span>
							</div>
						</div>
						<div className='flex justify-end'>
							<Button onClick={() => {
								setIconCurrent(null);
								setInfoAnchorEl(null);
							}} color="success">{t("buttons.close")}</Button>
						</div>
					</div>
				</Dialog>
			}

			{
				open && (
					<MapSettings
						anchorEl={anchorEl}
						handleClose={handleClose}
						handleApply={handleApply}
					/>
				)
			}

			{
				filterEventsOpen && (
					<FilterEvents
						anchorEl={filterEventsEl}
						handleClose={handleFilterEventsClose}
					/>
				)
			}
		</div>
	);
}

